import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select, Stack,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScienceIcon from '@mui/icons-material/Science';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/VpnKey';
import api from '../services/api';

/**
 * Bulk user operations from a CSV sheet — now, or on a schedule.
 *
 * The flow is deliberately three steps with a stop in the middle:
 * download the template, VALIDATE the filled sheet, then store or run it.
 * Validation is where an operator finds out that row 41 names a domain
 * controller that is not enrolled, and it is much better to find that out
 * before 02:00 tomorrow than at 02:00 tomorrow.
 */

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const OUTCOME_COLOR = { success: 'success', failure: 'error', skipped: 'default' };

function ScheduleFields({ form, setForm }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const kind = form.schedule_kind;

  return (
    <Stack spacing={2}>
      <FormControl fullWidth size="small">
        <InputLabel id="sched-kind">Schedule</InputLabel>
        <Select labelId="sched-kind" label="Schedule" value={kind}
                onChange={set('schedule_kind')}>
          <MenuItem value="manual">Manual — only when someone runs it</MenuItem>
          <MenuItem value="once">Once, at a date and time</MenuItem>
          <MenuItem value="hourly">Every hour</MenuItem>
          <MenuItem value="daily">Every day</MenuItem>
          <MenuItem value="weekly">Every week</MenuItem>
          <MenuItem value="monthly">Every month</MenuItem>
          <MenuItem value="cron">Cron expression</MenuItem>
        </Select>
      </FormControl>

      {kind === 'once' && (
        <TextField size="small" label="Run at" type="datetime-local"
                   InputLabelProps={{ shrink: true }}
                   value={form.run_at_local} onChange={set('run_at_local')} fullWidth />
      )}

      {kind === 'cron' && (
        <TextField size="small" label="Cron expression" value={form.schedule_cron}
                   onChange={set('schedule_cron')} fullWidth
                   placeholder="0 2 * * 1-5"
                   helperText="minute hour day-of-month month day-of-week" />
      )}

      {['hourly', 'daily', 'weekly', 'monthly'].includes(kind) && (
        <Stack direction="row" spacing={2}>
          {kind !== 'hourly' && (
            <TextField size="small" label="Hour" type="number" sx={{ width: 110 }}
                       inputProps={{ min: 0, max: 23 }}
                       value={form.schedule_hour} onChange={set('schedule_hour')} />
          )}
          <TextField size="small" label="Minute" type="number" sx={{ width: 110 }}
                     inputProps={{ min: 0, max: 59 }}
                     value={form.schedule_minute} onChange={set('schedule_minute')} />
          {kind === 'weekly' && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sched-dow">Day</InputLabel>
              <Select labelId="sched-dow" label="Day" value={form.schedule_weekday}
                      onChange={set('schedule_weekday')}>
                {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i}>{d}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {kind === 'monthly' && (
            <TextField size="small" label="Day of month" type="number" sx={{ width: 150 }}
                       inputProps={{ min: 1, max: 31 }}
                       value={form.schedule_day_of_month}
                       onChange={set('schedule_day_of_month')}
                       helperText="31 means the last day in shorter months" />
          )}
        </Stack>
      )}

      {kind !== 'manual' && kind !== 'once' && (
        <TextField size="small" label="Timezone" value={form.schedule_timezone}
                   onChange={set('schedule_timezone')} fullWidth
                   helperText="An IANA name, e.g. America/New_York. The time above is local to it, across daylight saving." />
      )}
    </Stack>
  );
}

function Upload({ spec, onStored, onError }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '',
    schedule_kind: 'manual', schedule_timezone: 'UTC',
    schedule_hour: 2, schedule_minute: 0, schedule_weekday: 0,
    schedule_day_of_month: 1, schedule_cron: '', run_at_local: '',
    skip_invalid_rows: false,
  });

  const blocking = useMemo(
    () => (preview?.errors || []).filter((e) => e.level !== 'warning'),
    [preview],
  );

  const download = async () => {
    try {
      const r = await api.get('/bulk/template.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bulk-users-template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      onError('Could not download the template.');
    }
  };

  const pick = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    if (f && !form.name) setForm((s) => ({ ...s, name: f.name.replace(/\.csv$/i, '') }));
  };

  const validate = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/bulk/validate/', fd);
      setPreview(r.data);
    } catch (e) {
      onError(e.response?.data?.error || 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  };

  const store = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', form.name || file.name);
      fd.append('description', form.description);
      fd.append('schedule_kind', form.schedule_kind);
      fd.append('schedule_timezone', form.schedule_timezone);
      fd.append('schedule_hour', form.schedule_hour);
      fd.append('schedule_minute', form.schedule_minute);
      fd.append('schedule_weekday', form.schedule_weekday);
      fd.append('schedule_day_of_month', form.schedule_day_of_month);
      fd.append('schedule_cron', form.schedule_cron);
      fd.append('enabled', 'true');
      if (form.skip_invalid_rows) fd.append('skip_invalid_rows', 'true');
      if (form.schedule_kind === 'once' && form.run_at_local) {
        // The picker gives local wall-clock time; the API stores UTC.
        fd.append('run_at', new Date(form.run_at_local).toISOString());
      }
      const r = await api.post('/bulk/jobs/', fd);
      setFile(null);
      setPreview(null);
      onStored(r.data);
    } catch (e) {
      onError(e.response?.data?.error
        || JSON.stringify(e.response?.data || 'Could not save the sheet.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>1 · Get the template</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The template is generated from the same column definition the validator reads,
            so a file you download today cannot be rejected tomorrow by a validator that
            moved on. One row is one action on one domain controller — a single sheet can
            create accounts on one DC and disable accounts on another.
          </Typography>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={download}>
            Download bulk-users-template.csv
          </Button>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Column</TableCell>
                  <TableCell>Required</TableCell>
                  <TableCell>Applies to</TableCell>
                  <TableCell>Meaning</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(spec?.columns || []).map((c) => (
                  <TableRow key={c.name}>
                    <TableCell><code>{c.name}</code></TableCell>
                    <TableCell>{c.required ? 'yes' : ''}</TableCell>
                    <TableCell>
                      {c.actions.length === 3 ? 'all' : c.actions.join(', ')}
                    </TableCell>
                    <TableCell>{c.help}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Alert severity="info" sx={{ mt: 2 }}>
            Enrolled agents you can name in the <code>agent</code> column:{' '}
            {(spec?.agents || []).map((a) => (
              <Chip key={a} label={a} size="small" sx={{ mr: 0.5 }} />
            ))}
          </Alert>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>2 · Check your sheet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Nothing is sent to a domain controller here. Every row is checked and you see
            exactly what would happen.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
              {file ? file.name : 'Choose CSV'}
              <input hidden type="file" accept=".csv,text/csv" onChange={pick} />
            </Button>
            <Button variant="contained" onClick={validate} disabled={!file || busy}>
              {busy ? <CircularProgress size={20} /> : 'Validate'}
            </Button>
          </Stack>

          {preview && (
            <Box sx={{ mt: 3 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip color={preview.valid ? 'success' : 'error'}
                      label={preview.valid ? 'No blocking errors' : `${blocking.length} error(s)`} />
                <Chip label={`${preview.row_count} usable row(s)`} />
                {Object.entries(preview.summary || {}).map(([k, v]) => (
                  <Chip key={k} variant="outlined" label={`${k}: ${v}`} />
                ))}
              </Stack>

              {(preview.errors || []).length > 0 && (
                <Alert severity={preview.valid ? 'warning' : 'error'} sx={{ mb: 2 }}>
                  <AlertTitle>Rows that need attention</AlertTitle>
                  {preview.errors.map((e, i) => (
                    <div key={i}>line {e.line}: {e.message}</div>
                  ))}
                </Alert>
              )}

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 340 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(preview.rows || []).map((r) => (
                      <TableRow key={`${r.line}-${r.username}-${r.action}`}>
                        <TableCell>{r.line}</TableCell>
                        <TableCell><Chip size="small" label={r.agent} /></TableCell>
                        <TableCell>{r.action}</TableCell>
                        <TableCell>{r.username}</TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                          {['first_name', 'surname', 'email', 'ou', 'groups']
                            .filter((k) => r[k])
                            .map((k) => `${k}=${Array.isArray(r[k]) ? r[k].join(';') : r[k]}`)
                            .join('  ')}
                          {r.password ? '  password=<supplied>' : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      {preview && preview.row_count > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>3 · Name it and say when</Typography>
            <Stack spacing={2}>
              <TextField size="small" label="Name" value={form.name} fullWidth
                         onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              <TextField size="small" label="Description" value={form.description} fullWidth
                         onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              <ScheduleFields form={form} setForm={setForm} />
              {blocking.length > 0 && (
                <FormControlLabel
                  control={<Switch checked={form.skip_invalid_rows}
                                   onChange={(e) => setForm((f) => ({ ...f, skip_invalid_rows: e.target.checked }))} />}
                  label={`Store anyway and skip the ${blocking.length} bad row(s)`} />
              )}
              <Box>
                <Button variant="contained" onClick={store}
                        disabled={busy || (blocking.length > 0 && !form.skip_invalid_rows)}>
                  Save sheet
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Saving does not run it. A manual sheet waits for you; a scheduled one runs
                at the time above. Passwords left blank are generated at run time and shown
                once, in the run result.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

function RunResult({ run, onCollect }) {
  if (!run) return null;
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
        <Chip size="small" color={run.failed ? 'error' : 'success'}
              label={`${run.succeeded} ok / ${run.failed} failed / ${run.skipped} skipped of ${run.total}`} />
        {run.dry_run && <Chip size="small" label="dry run" />}
        <Chip size="small" variant="outlined" label={`${run.trigger} · ${fmt(run.started_at)}`} />
        {run.has_credentials && (
          <Button size="small" startIcon={<KeyIcon />} onClick={() => onCollect(run)}>
            Collect generated passwords
          </Button>
        )}
      </Stack>
      {run.error && <Alert severity="error" sx={{ mb: 1 }}>{run.error}</Alert>}
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Line</TableCell><TableCell>Agent</TableCell>
              <TableCell>Action</TableCell><TableCell>Username</TableCell>
              <TableCell>Outcome</TableCell><TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(run.results || []).map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.line}</TableCell>
                <TableCell>{r.agent}</TableCell>
                <TableCell>{r.action}</TableCell>
                <TableCell>{r.username}</TableCell>
                <TableCell>
                  <Chip size="small" color={OUTCOME_COLOR[r.outcome] || 'default'}
                        label={r.outcome} />
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function Sheets({ onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [creds, setCreds] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/bulk/jobs/');
      setRows(r.data || []);
    } catch (e) {
      onError('Could not load stored sheets.');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const run = async (job, dryRun) => {
    setBusyId(job.id);
    try {
      const r = await api.post(`/bulk/jobs/${job.id}/run/`, { dry_run: dryRun });
      setOpenRun(r.data);
      load();
    } catch (e) {
      onError(e.response?.data?.error || 'The run failed to start.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.name}"? Accounts it already created are not touched.`)) return;
    try {
      await api.delete(`/bulk/jobs/${job.id}/`);
      load();
    } catch (e) {
      onError('Could not delete that sheet.');
    }
  };

  const toggle = async (job) => {
    try {
      await api.patch(`/bulk/jobs/${job.id}/`, { enabled: !job.enabled });
      load();
    } catch (e) {
      onError('Could not change that schedule.');
    }
  };

  const collect = async (r) => {
    try {
      const res = await api.post(`/bulk/runs/${r.id}/credentials/`);
      setCreds(res.data.credentials || []);
      setOpenRun((prev) => (prev ? { ...prev, has_credentials: false } : prev));
    } catch (e) {
      onError(e.response?.data?.error || 'Could not collect those passwords.');
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          A stored sheet keeps its rows; running it again applies them again.
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      {rows.length === 0 && (
        <Alert severity="info">No sheets stored yet. Upload one on the first tab.</Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Rows</TableCell>
              <TableCell>Agents</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Next run</TableCell>
              <TableCell>Last run</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((j) => (
              <TableRow key={j.id} hover>
                <TableCell>
                  <Typography variant="body2">{j.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {j.source_filename} · by {j.created_by_username || 'unknown'}
                  </Typography>
                </TableCell>
                <TableCell>{j.row_count}</TableCell>
                <TableCell>
                  {(j.agents || []).map((a) => (
                    <Chip key={a} size="small" label={a} sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{j.schedule_text}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{fmt(j.next_run_at)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {fmt(j.last_run_at)}
                  {j.last_run && (
                    <Chip size="small" sx={{ ml: 1 }}
                          color={j.last_run.failed ? 'error' : 'success'}
                          label={`${j.last_run.succeeded}/${j.last_run.total}`} />
                  )}
                </TableCell>
                <TableCell>
                  <Switch size="small" checked={j.enabled} onChange={() => toggle(j)}
                          disabled={j.schedule_kind === 'manual'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Dry run — check every row, send nothing">
                    <span>
                      <IconButton size="small" onClick={() => run(j, true)} disabled={busyId === j.id}>
                        <ScienceIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Run now">
                    <span>
                      <IconButton size="small" color="primary"
                                  onClick={() => run(j, false)} disabled={busyId === j.id}>
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Delete sheet">
                    <IconButton size="small" color="error" onClick={() => remove(j)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!openRun} onClose={() => setOpenRun(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Run result</DialogTitle>
        <DialogContent>
          <RunResult run={openRun} onCollect={collect} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRun(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!creds} onClose={() => setCreds(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Generated passwords</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            These are shown once and have now been destroyed on the server. Copy them
            before closing this dialog — if they are lost, reset the passwords instead.
          </Alert>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Agent</TableCell><TableCell>Username</TableCell><TableCell>Password</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(creds || []).map((c, i) => (
                  <TableRow key={i}>
                    <TableCell>{c.agent}</TableCell>
                    <TableCell>{c.username}</TableCell>
                    <TableCell><code>{c.password}</code></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreds(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function History({ onError }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/bulk/runs/');
      setRuns(r.data || []);
    } catch (e) {
      onError('Could not load run history.');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell><TableCell>Trigger</TableCell>
              <TableCell>By</TableCell><TableCell>Result</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{fmt(r.started_at)}</TableCell>
                <TableCell>{r.trigger}{r.dry_run ? ' (dry)' : ''}</TableCell>
                <TableCell>{r.triggered_by_username || 'schedule'}</TableCell>
                <TableCell>
                  <Chip size="small" color={r.failed ? 'error' : 'success'}
                        label={`${r.succeeded} ok / ${r.failed} failed / ${r.skipped} skipped`} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => setOpen(r)}>Details</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Run detail</DialogTitle>
        <DialogContent><RunResult run={open} onCollect={() => {}} /></DialogContent>
        <DialogActions><Button onClick={() => setOpen(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default function BulkOperations() {
  const [tab, setTab] = useState(0);
  const [spec, setSpec] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/bulk/spec/')
      .then((r) => setSpec(r.data))
      .catch(() => setError('Could not load the column definition.'));
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Bulk user operations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create, enable and disable accounts from one CSV sheet — immediately, or on a
        schedule. Each row names the domain controller it applies to, so a single sheet
        can cover several at once.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Upload a sheet" />
        <Tab label="Stored sheets & schedules" />
        <Tab label="Run history" />
      </Tabs>

      {tab === 0 && <Upload spec={spec} onError={setError}
                            onStored={() => setTab(1)} />}
      {tab === 1 && <Sheets onError={setError} />}
      {tab === 2 && <History onError={setError} />}
    </Box>
  );
}
